import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {View, StyleSheet, FlatList, Alert} from 'react-native';
import {
  Text,
  ActivityIndicator,
  Snackbar,
  FAB,
  Menu,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import GradientHeader from '../../components/common/GradientHeader';
import {useNavigation, DrawerActions, useFocusEffect} from '@react-navigation/native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import {useExpenseStore} from '../../store/useExpenseStore';
import {useExpenseCategoryStore} from '../../store/useExpenseCategoryStore';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import OrgUnitSelector from '../../components/common/OrgUnitSelector';
import {colors, spacing} from '../../theme';

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return {day: String(d.getDate()), month: MONTHS[d.getMonth()]};
};

const DURATION_KEYS = [
  'all',
  'today',
  'yesterday',
  'thisWeek',
  'lastWeek',
  'thisMonth',
  'previousMonth',
  'last3Months',
];

const computeDateRange = key => {
  const today = new Date();
  const startOfDay = d => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const fmt = d =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  switch (key) {
    case 'today':
      return {from: fmt(today), to: fmt(today)};
    case 'yesterday': {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      return {from: fmt(y), to: fmt(y)};
    }
    case 'thisWeek': {
      const d = startOfDay(today);
      const day = d.getDay();
      const diff = day === 0 ? 6 : day - 1;
      d.setDate(d.getDate() - diff);
      return {from: fmt(d), to: fmt(today)};
    }
    case 'lastWeek': {
      const d = startOfDay(today);
      const day = d.getDay();
      const diff = day === 0 ? 6 : day - 1;
      const thisMonday = new Date(d);
      thisMonday.setDate(d.getDate() - diff);
      const lastMonday = new Date(thisMonday);
      lastMonday.setDate(thisMonday.getDate() - 7);
      const lastSunday = new Date(thisMonday);
      lastSunday.setDate(thisMonday.getDate() - 1);
      return {from: fmt(lastMonday), to: fmt(lastSunday)};
    }
    case 'thisMonth': {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      return {from: fmt(first), to: fmt(today)};
    }
    case 'previousMonth': {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      return {from: fmt(first), to: fmt(last)};
    }
    case 'last3Months': {
      const d = new Date(today);
      d.setMonth(d.getMonth() - 3);
      return {from: fmt(d), to: fmt(today)};
    }
    default:
      return {};
  }
};

const ExpenseListScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {expenses, isLoading, fetchExpenses} = useExpenseStore();
  const {expenseCategories, fetchExpenseCategories} = useExpenseCategoryStore();
  const {selectedOrgUnitId} = useOrgUnitStore();

  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState('');
  const [durationKey, setDurationKey] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [durationMenuVisible, setDurationMenuVisible] = useState(false);
  const [categoryMenuVisible, setCategoryMenuVisible] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);

  const loadExpenses = useCallback(
    async (durKey = durationKey) => {
      try {
        const range = computeDateRange(durKey);
        await fetchExpenses({...range, orgUnitId: selectedOrgUnitId});
      } catch (e) {
        setSnackbar(e.response?.data?.error?.message || t('common.error'));
      }
    },
    [durationKey, fetchExpenses, selectedOrgUnitId, t],
  );

  useFocusEffect(
    useCallback(() => {
      loadExpenses();
      fetchExpenseCategories();
    }, [loadExpenses, fetchExpenseCategories]),
  );

  useEffect(() => {
    loadExpenses();
  }, [selectedOrgUnitId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadExpenses();
    setRefreshing(false);
  }, [loadExpenses]);

  const handleDurationChange = useCallback(
    key => {
      setDurationKey(key);
      setDurationMenuVisible(false);
      loadExpenses(key);
    },
    [loadExpenses],
  );

  const handleCategoryChange = useCallback(
    catId => {
      setCategoryFilter(catId);
      setCategoryMenuVisible(false);
    },
    [],
  );

  const filteredExpenses = useMemo(() => {
    if (!categoryFilter) return expenses;
    return expenses.filter(e => e.expenseCategoryId === categoryFilter);
  }, [expenses, categoryFilter]);

  const handleDelete = useCallback(
    expense => {
      Alert.alert(
        t('common.delete'),
        t('expense.deleteConfirm'),
        [
          {text: t('common.cancel'), style: 'cancel'},
          {
            text: t('common.delete'),
            style: 'destructive',
            onPress: async () => {
              try {
                await useExpenseStore.getState().deleteExpense(expense.id);
              } catch (e) {
                setSnackbar(
                  e.response?.data?.error?.message || t('common.error'),
                );
              }
            },
          },
        ],
      );
    },
    [t],
  );

  const renderItem = useCallback(
    ({item}) => {
      const date = parseDate(item.expenseDate);
      const displayAmount = item.amount ?? item.totalAmount;
      const displayPurpose = item.purpose ?? item.description ?? '';
      const displayMerchant = item.merchant ?? item.vendorName;
      return (
        <View style={styles.card}>
          <View style={styles.dateCol}>
            {date ? (
              <>
                <Text style={styles.dateDay}>{date.day}</Text>
                <Text style={styles.dateMonth}>{date.month}</Text>
              </>
            ) : (
              <Text style={styles.datePlaceholder}>{'\u2014'}</Text>
            )}
          </View>
          <View style={styles.cardCenter}>
            <Text style={styles.cardPurpose} numberOfLines={1}>
              {displayPurpose}
            </Text>
            {displayMerchant ? (
              <Text style={styles.cardDetail} numberOfLines={1}>
                TO / {displayMerchant}
              </Text>
            ) : null}
            <Text style={styles.cardDetail} numberOfLines={1}>
              {item.expenseCategoryName || ''}
              {item.staffName ? ` \u2022 ${item.staffName}` : ''}
              {item.paymentMode ? ` \u2022 ${item.paymentMode}` : ''}
            </Text>
          </View>
          <View style={styles.cardRight}>
            <Text style={styles.cardAmount}>
              -{'\u20B9'}{displayAmount}
            </Text>
            <View style={styles.cardActions}>
              <MaterialCommunityIcons
                name="pencil-outline"
                size={18}
                color={colors.textSecondary}
                onPress={() =>
                  navigation.navigate('ExpenseForm', {expense: item})
                }
                style={styles.actionIcon}
              />
              <MaterialCommunityIcons
                name="delete-outline"
                size={18}
                color={colors.textSecondary}
                onPress={() => handleDelete(item)}
                style={styles.actionIcon}
              />
            </View>
          </View>
        </View>
      );
    },
    [navigation, handleDelete],
  );

  const keyExtractor = useCallback(item => String(item.id), []);

  const selectedCategoryName = useMemo(() => {
    if (!categoryFilter) return t('expense.category');
    const cat = expenseCategories.find(c => c.id === categoryFilter);
    return cat ? cat.name : t('expense.category');
  }, [categoryFilter, expenseCategories, t]);

  if (isLoading && expenses.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <GradientHeader
        title={t('expense.title')}
        onMenu={() => navigation.dispatch(DrawerActions.openDrawer())}>
        <OrgUnitSelector variant="light" />
      </GradientHeader>

      {/* Filter bar */}
      <View style={styles.filterBar}>
        <Menu
          visible={durationMenuVisible}
          onDismiss={() => setDurationMenuVisible(false)}
          anchor={
            <View style={styles.filterChip}>
              <Text
                style={styles.filterChipText}
                onPress={() => setDurationMenuVisible(true)}>
                {t(`expense.duration.${durationKey}`)}
              </Text>
              <MaterialCommunityIcons
                name="chevron-down"
                size={18}
                color={colors.textSecondary}
                onPress={() => setDurationMenuVisible(true)}
              />
            </View>
          }>
          {DURATION_KEYS.map(key => (
            <Menu.Item
              key={key}
              title={t(`expense.duration.${key}`)}
              onPress={() => handleDurationChange(key)}
            />
          ))}
        </Menu>

        <Menu
          visible={categoryMenuVisible}
          onDismiss={() => setCategoryMenuVisible(false)}
          anchor={
            <View style={styles.filterChip}>
              <Text
                style={styles.filterChipText}
                onPress={() => setCategoryMenuVisible(true)}
                numberOfLines={1}>
                {selectedCategoryName}
              </Text>
              <MaterialCommunityIcons
                name="chevron-down"
                size={18}
                color={colors.textSecondary}
                onPress={() => setCategoryMenuVisible(true)}
              />
            </View>
          }>
          <Menu.Item
            title={t('expense.duration.all')}
            onPress={() => handleCategoryChange(null)}
          />
          {expenseCategories.map(cat => (
            <Menu.Item
              key={cat.id}
              title={cat.name}
              onPress={() => handleCategoryChange(cat.id)}
            />
          ))}
        </Menu>

        <MaterialCommunityIcons
          name="filter-variant"
          size={24}
          color={colors.primary}
          onPress={() => navigation.navigate('ExpenseSearchFilters')}
          style={styles.filterIcon}
        />
      </View>

      <FlatList
        data={filteredExpenses}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        contentContainerStyle={styles.listContent}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <MaterialCommunityIcons name="cash-register" size={48} color={colors.textLight} />
            <Text style={styles.emptyText}>{t('expense.noExpenses')}</Text>
          </View>
        }
        refreshing={refreshing}
        onRefresh={handleRefresh}
      />

      <FAB.Group
        open={fabOpen}
        visible
        icon={fabOpen ? 'close' : 'plus'}
        actions={[
          {
            icon: 'plus',
            label: t('expense.addExpense'),
            onPress: () => navigation.navigate('ExpenseForm'),
          },
          {
            icon: 'shape',
            label: t('expense.categories'),
            onPress: () => navigation.navigate('ExpenseCategoryList'),
          },
        ]}
        onStateChange={({open}) => setFabOpen(open)}
        fabStyle={styles.fab}
        color={colors.white}
      />

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  filterBar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.surface,
  },
  filterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 20,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    marginRight: spacing.sm,
  },
  filterChipText: {
    fontSize: 13,
    color: colors.text,
    marginRight: 4,
  },
  filterIcon: {
    marginLeft: 'auto',
    padding: spacing.xs,
  },
  listContent: {padding: spacing.sm, paddingBottom: 80, gap: spacing.sm},
  card: {
    flexDirection: 'row',
    backgroundColor: colors.surface,
    paddingVertical: spacing.sm + 2,
    paddingHorizontal: spacing.md,
    alignItems: 'center',
    borderRadius: 10,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  dateCol: {
    width: 48,
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  dateDay: {fontSize: 24, fontWeight: '400', color: colors.text, lineHeight: 28},
  dateMonth: {fontSize: 11, color: colors.textSecondary, fontWeight: '600', letterSpacing: 1},
  datePlaceholder: {fontSize: 24, color: colors.textSecondary},
  cardCenter: {
    flex: 1,
  },
  cardPurpose: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.text,
  },
  cardDetail: {
    fontSize: 12,
    color: colors.textSecondary,
    marginTop: 2,
  },
  cardRight: {
    alignItems: 'flex-end',
    marginLeft: spacing.sm,
  },
  cardAmount: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.error,
  },
  cardActions: {
    flexDirection: 'row',
    marginTop: 4,
  },
  actionIcon: {
    marginLeft: spacing.sm,
  },
  emptyContainer: {
    alignItems: 'center',
    paddingTop: spacing.xxl * 2,
  },
  emptyText: {
    color: colors.textSecondary,
    marginTop: spacing.sm,
    fontSize: 14,
  },
  fab: {
    backgroundColor: colors.primary,
  },
});

export default ExpenseListScreen;
